import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtCurrency } from '../../lib/display-labels';
import {
  SUMMARY_GROUP_BY_LABELS,
  parseCostSummary,
  type CostFigures,
  type CostGroup,
  type SummaryGroupBy,
} from '../../lib/expenses';

/**
 * APPROVED expense cost — GET /expenses/summary (api#1023), FE-2.
 *
 * EVERY FIGURE IS THE API'S. The net is computed server-side as amount − GST,
 * over receipts whose GST is KNOWN; a receipt with no GST recorded is counted
 * separately as "GST unknown" and is in neither net nor GST recovered. This
 * panel renders those figures verbatim and recomputes nothing — a client-side
 * net would be a second answer to the same question. An absent figure renders
 * `—`, never `$0.00`.
 *
 * THE "NO JOB" / "NO CUSTOMER" ROW ALWAYS RENDERS when the API sends it, even
 * at zero: general expenses (a sales lunch) live there, and a dropped row would
 * quietly shrink the visible cost below the total printed beneath it.
 *
 * Office view roles only — the API 403s everyone else, and a refusal renders as
 * a refusal (LoadFailureNotice), never as "no approved expenses".
 */
export default function ExpenseCostSummary() {
  const [groupBy, setGroupBy] = useState<SummaryGroupBy>('job');

  const query = useQuery({
    queryKey: ['expenses', 'summary', groupBy],
    // group_by is OMITTED for 'job', the API's default — the exact request the
    // endpoint has always answered.
    queryFn: () =>
      api.expenses.summary(groupBy === 'job' ? undefined : { group_by: groupBy }).then((r) => parseCostSummary(r.data, groupBy)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const data = query.data;

  const rows: CostGroup[] = data ? [...data.groups, ...(data.unassigned ? [data.unassigned] : [])] : [];

  return (
    <section
      data-testid="expense-cost-summary"
      aria-labelledby="expense-cost-summary-title"
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card"
    >
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Approved cost</p>
          <h2 id="expense-cost-summary-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
            Expense cost, net of GST
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Group cost by">
          {(Object.keys(SUMMARY_GROUP_BY_LABELS) as SummaryGroupBy[]).map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={groupBy === g}
              onClick={() => setGroupBy(g)}
              className={`rounded-fi-pill border px-3 py-1.5 text-xs font-bold transition-colors ${
                groupBy === g
                  ? 'border-fi-amber bg-fieldiq-card text-fieldiq-text'
                  : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
              }`}
            >
              {SUMMARY_GROUP_BY_LABELS[g]}
            </button>
          ))}
        </div>
      </header>

      {query.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-fieldiq-muted">
          <Loader2 size={18} className="animate-spin" /> Loading cost…
        </div>
      ) : failure ? (
        <LoadFailureNotice
          error={queryFailureError(query)}
          kind={failure}
          subject="expense cost"
          allowed="Admins, office staff and dispatchers"
        />
      ) : !data || (rows.length === 0 && (data.totals.count ?? 0) === 0) ? (
        <p className="px-4 py-10 text-center text-sm text-fieldiq-muted">No approved expenses yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">{groupBy === 'customer' ? 'Customer' : groupBy === 'user' ? 'Person' : 'Job'}</th>
                <th scope="col" className="px-4 py-3 text-right">Receipts</th>
                <th scope="col" className="px-4 py-3 text-right">Gross</th>
                <th scope="col" className="px-4 py-3 text-right">Net</th>
                <th scope="col" className="px-4 py-3 text-right">GST recovered</th>
                <th scope="col" className="px-4 py-3 text-right">GST unknown</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <FiguresRow key={g.key} testId={`cost-row-${g.key}`} label={g.label} deleted={g.deleted} figures={g.figures} />
              ))}
            </tbody>
            <tfoot className="border-t-2 border-fieldiq-border font-bold">
              <FiguresRow testId="cost-row-total" label="Total" deleted={false} figures={data.totals} />
            </tfoot>
          </table>
          <div className="space-y-1 px-4 py-3 text-xs text-fieldiq-muted">
            {data.notes.gstUnknown && (
              <p data-testid="cost-gst-unknown-note" className="font-medium text-fi-signal-amber">{data.notes.gstUnknown}</p>
            )}
            {data.notes.netBasis && <p>{data.notes.netBasis}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

function FiguresRow({ testId, label, deleted, figures }: { testId: string; label: string; deleted: boolean; figures: CostFigures }) {
  const unknown = figures.gstUnknownCount;
  return (
    <tr data-testid={testId} className="border-t border-fieldiq-border">
      <td className="px-4 py-3 text-fieldiq-text">
        {label}
        {deleted && (
          <span className="ml-2 rounded bg-fi-amber/15 px-1.5 py-0.5 text-[11px] text-fi-signal-amber">deleted</span>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{figures.count ?? '—'}</td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{fmtCurrency(figures.gross)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text" data-testid={`${testId}-net`}>{fmtCurrency(figures.netKnown)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{fmtCurrency(figures.gstRecovered)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text" data-testid={`${testId}-gst-unknown`}>
        {unknown === null ? '—' : unknown === 0 ? '0' : `${unknown} · ${fmtCurrency(figures.gstUnknownGross)}`}
      </td>
    </tr>
  );
}
